import { useState, useEffect } from 'react';
import {
  Button,
  Container,
  Row,
  Col,
  Form as BootstrapForm,
  Dropdown,
  Modal,
  Table,
  OverlayTrigger, Tooltip ,
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import * as Yup from 'yup';
import { Formik, Form as FormikForm, Field, ErrorMessage } from 'formik';
import Sidebar from '../../components/Sidebar';
import DeleteDialog from '../../components/DeleteDialog';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const getValidationSchema = (userType) =>
  Yup.object().shape({
    name: Yup.string()
      .required('Name is required')
      .test(
        'no-empty-or-spaces',
        'Name cannot be empty or spaces only',
        (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
      ),
    email: Yup.string()
  .required('Email is required')
  .matches(
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    'Invalid email format'
  )
  .test(
    'no-empty-or-spaces',
    'Email cannot be empty or spaces only',
    (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
  ),
    password: Yup.string()
      .required('Password is required')
      .min(6, 'Password must be at least 6 characters')
      .matches(/^\S*$/, 'Password cannot contain spaces or tabs'),
    contactNumber: Yup.string()
      .required('Contact number is required')
      .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits'),
    subHospitalId: Yup.string().required('Subhospital is required'),
    specialties: userType === 'Doctor'
      ? Yup.string().required('Specialties required')
      : Yup.string(),
    qualifications: userType === 'Doctor'
      ? Yup.string().required('Qualifications required')
      : Yup.string(),
    labName: userType === 'Lab'
      ? Yup.string().required('Lab name required')
      : Yup.string(),
    address: ['Receptionist', 'Lab'].includes(userType)
      ? Yup.string().required('Address required')
      : Yup.string(),
  });

const preventSpaces = (e) => {
  if (
    e.key === ' ' &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
  }
};
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};

const ManageUsers = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [userType, setUserType] = useState('');
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [subHospitalId, setSubHospitalId] = useState(state?.subHospitalId || '');
  const [hospitalId, setHospitalId] = useState('');
  const [subHospitals, setSubHospitals] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  // For table filters
  const [filters, setFilters] = useState({
    name: '',
    subHospital: '',
    role: '',
    contact: '',
    email: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const storedHospitalId = localStorage.getItem('hospitalId');
    if (storedHospitalId) {
      setHospitalId(storedHospitalId);
    }
  }, [navigate]);

  useEffect(() => {
    if (hospitalId) {
      fetchSubHospitals(hospitalId);
      fetchUsers(hospitalId);
    }
  }, [hospitalId]);

  const fetchUsers = async (hospId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token found');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.get(
        `${BASE_URL}/api/admin/allUsersByHospital/${hospId}`,
        config
      );
      const normalizedUsers = (response.data || []).map((user) => {
        if (user.userType === 'Lab') {
          return { ...user, name: user.labName || user.lab_name || user.name };
        }
        return user;
      });
      setUsers(normalizedUsers);
    } catch (error) {
      setUsers([]);
      toast.error('Error fetching users');
    }
  };

  const fetchSubHospitals = async (hospitalId) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/subHospitals/getSubHospitalsByHospital/${hospitalId}`
      );
      setSubHospitals(response.data);
    } catch (error) {
      toast.error('Error fetching subhospitals');
    }
  };

  const handleDropdownSelect = (eventKey) => {
    setUserType(eventKey);
    setEditingUser(null);
    setShowModal(true);
    setSubHospitalId('');
  };

  const handleSubmitUser = async (values, { resetForm }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token found');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };
      const newUser = {
        ...values,
        userType,
        hospitalId: localStorage.getItem('hospitalId'),
        subHospitalId: values.subHospitalId,
        role:
          userType === 'Doctor'
            ? 2
            : userType === 'Receptionist'
              ? 3
              : userType === 'Lab'
                ? 5
                : null,
      };
      if (editingUser) {
        if (!values.password) {
          delete newUser.password;
        }
        await axios.put(
          `${BASE_URL}/api/admin/editUser/${userType}/${editingUser.id}`,
          newUser,
          config
        );
        toast.success(`${userType} updated successfully!`);
      } else {
        const endpoint =
          userType === 'Doctor'
            ? 'addDoctor'
            : userType === 'Receptionist'
              ? 'addReceptionist'
              : userType === 'Lab'
                ? 'addLab'
                : '';
        if (endpoint) {
          await axios.post(
            `${BASE_URL}/api/admin/${endpoint}`,
            newUser,
            config
          );
          toast.success(`${userType} added successfully!`);
        }
      }
      resetForm();
      setShowModal(false);
      setEditingUser(null);
      fetchUsers(hospitalId);
    } catch (error) {
      if (error.response) {
        toast.error(error.response.data.message || 'Error');
      } else {
        toast.error('Unexpected error');
      }
    }
  };

const handleEditUser = async (index) => {
  const user = users[index];
  console.log("Editing user:", user);

  setUserType(user.userType);

  let apiUrl = "";

  if (user.userType === "Doctor") {
    apiUrl = `${BASE_URL}/api/admin/doctor/${user.id}`;
  } else if (user.userType === "Receptionist") {
    apiUrl = `${BASE_URL}/api/admin/receptionist/${user.id}`;
  } else if (user.userType === "Lab") {
    apiUrl = `${BASE_URL}/api/admin/lab/${user.id}`;
  }

  try {
    const token = localStorage.getItem("token");
    if (!token) throw new Error("No auth token found");

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    };

    let fullData = {};

    if (apiUrl) {
      const res = await axios.get(apiUrl, config);
      fullData = res.data || {};
    }

    const mergedData = {
      ...user,        // login table data
      ...fullData,    // doctor/lab/receptionist table data
    };

    console.log("Merged final user data:", mergedData);

    setEditingUser(mergedData);
    setHospitalId(mergedData.hospital_id || "");
    setSubHospitalId(mergedData.sub_hospital_id || "");

    setShowModal(true);
  } catch (error) {
    console.error(error);
    toast.error("Failed to fetch user details for editing.");
  }
};


  const handleToggleUserStatus = async (user, status) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No auth token found');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };
      const payload = {
        userId: user.id,
        status,
      };
      
      await axios.put(`${BASE_URL}/api/admin/toggleUserStatus`, payload, config);
      toast.success(        
        `User ${status === 1 ? 'enabled' : 'disabled'} successfully!`
      );
      setUsers((prev) =>
        prev.map((u) => (u.id === user.id ? { ...u, status } : u))
      );
    } catch {
      toast.error('Error toggling user status.');
    }
  };

  const filteredUsers = users.filter(
    (user) =>
      (!filters.name ||
        (user.name || '').toLowerCase().includes(filters.name.toLowerCase())) &&
      (!filters.subHospital ||
        String(user.sub_hospital_id) === String(filters.subHospital)) &&
      (!filters.role || user.userType === filters.role) &&
      (!filters.contact ||
        (user.contactNumber || '').includes(filters.contact)) &&
      (!filters.email ||
        (user.email || '').toLowerCase().includes(filters.email.toLowerCase()))
  );

  return (
    <div className="d-flex">

      <Container fluid>
        <Row className="mb-4 pt-3 pb-4 align-items-center">
          <Col xs={12} md={6} className="d-flex align-items-center">
            <h3 className="global-heading mb-0">Manage Users</h3>
          </Col>
          <Col
            xs={12}
            md={6}
            className="d-flex justify-content-md-end justify-content-start mt-3 mt-md-0"
          >
            <Dropdown onSelect={handleDropdownSelect}>
              <Dropdown.Toggle className="global-btn" id="dropdown-basic">
                {userType || 'Select User Type'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item eventKey="Doctor">Doctor</Dropdown.Item>
                <Dropdown.Item eventKey="Receptionist">
                  Receptionist
                </Dropdown.Item>
                <Dropdown.Item eventKey="Lab">Lab</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        <Modal
          show={showModal}
          onHide={() => {
            setShowModal(false);
            setEditingUser(null);
          }}
          centered
          backdrop="static"
          className="form-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title className="global-heading" style={{ fontSize: '1.2rem' }}>
              {editingUser ? `Edit ${userType}` : `Add ${userType}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Formik
              enableReinitialize
              initialValues={{
                name: editingUser?.name || '',
                email: editingUser?.email || '',
                password: '',
                contactNumber: editingUser?.contactNumber || '',
                subHospitalId: subHospitalId || '',
                specialties: editingUser?.specialties || '',
                qualifications: editingUser?.qualifications || '',
                labName: editingUser?.labName || '',
                address: editingUser?.address || '',
              }}
              validationSchema={getValidationSchema(userType)}
              onSubmit={handleSubmitUser}
            >
              {({ values }) => (
                
                <FormikForm>
                  <Row>
                    <Col md={12}>
                      <BootstrapForm.Group controlId="name">
                        <BootstrapForm.Label 
                        className="fw-semibold primary-class"
                        
                        >Name</BootstrapForm.Label>
                        <Field
                          name="name"
                          as={BootstrapForm.Control}
                          onKeyDown={preventSpaces}
                          onPaste={preventPasteSpaces}
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="text-danger"
                        />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12}>
                      <BootstrapForm.Group controlId="email">
                        <BootstrapForm.Label
                        className="fw-semibold primary-class"
                        >
                        Email</BootstrapForm.Label>
                          <Field
                            name="email"
                            type="email"
                            as={BootstrapForm.Control}
                            onKeyDown={(e) => {
                              if (e.key === " ") {
                                e.preventDefault(); // prevent spaces while typing
                              }
                            }}
                            onPaste={(e) => {
                              const pasteData = e.clipboardData.getData("text");
                              if (/\s/.test(pasteData)) {
                                e.preventDefault(); // prevent pasting spaces
                              }
                            }}
                          />

                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-danger"
                        />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12}>
                      <BootstrapForm.Group controlId="password">
                        <BootstrapForm.Label
                        className="fw-semibold primary-class"
                  >
                    Password</BootstrapForm.Label>
                        <Field
                          name="password"
                          type="password"
                          as={BootstrapForm.Control}
                          onKeyDown={(e) => {
                            if (e.key === ' ' || e.key === 'Tab') {
                              e.preventDefault();
                            }
                          }}
                          onPaste={preventPasteSpaces}
                        />
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-danger"
                        />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12}>
                      <BootstrapForm.Group controlId="contactNumber">
                        <BootstrapForm.Label
                        className="fw-semibold primary-class var(--primary-color)"
                  >
                    Contact Number</BootstrapForm.Label>
                        <Field
                        name="contactNumber"
                        as={BootstrapForm.Control}
                        maxLength={10}
                        onKeyDown={(e) => {
                          // Prevent spaces
                          if (e.key === " " || e.key === "Tab") {
                            e.preventDefault();
                          }
                          // Allow only digits + backspace/delete/arrow keys
                          if (
                            !/[0-9]/.test(e.key) &&
                            e.key !== "Backspace" &&
                            e.key !== "Delete" &&
                            e.key !== "ArrowLeft" &&
                            e.key !== "ArrowRight" &&
                            e.key !== "Tab"
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const pasteData = e.clipboardData.getData("text");
                          if (!/^\d*$/.test(pasteData)) {
                            e.preventDefault();
                          }
                        }}
                      />
                        <ErrorMessage
                          name="contactNumber"
                          component="div"
                          className="text-danger"
                        />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>
                  {userType === 'Doctor' && (
                    <Row className="mt-3">
                      <Col md={6}>
                        <BootstrapForm.Group controlId="specialties">
                          <BootstrapForm.Label
                          className="fw-semibold primary-class"
                    >
                      Specialties</BootstrapForm.Label>
                          <Field
                            name="specialties"
                            as={BootstrapForm.Control}
                            onKeyDown={preventSpaces}
                            onPaste={preventPasteSpaces}
                          />
                          <ErrorMessage
                            name="specialties"
                            component="div"
                            className="text-danger"
                          />
                        </BootstrapForm.Group>
                      </Col>
                      <Col md={6}>
                        <BootstrapForm.Group controlId="qualifications">
                          <BootstrapForm.Label
                          className="fw-semibold primary-class"
                    >
                      Qualifications</BootstrapForm.Label>
                          <Field
                            name="qualifications"
                            as={BootstrapForm.Control}
                            onKeyDown={preventSpaces}
                            onPaste={preventPasteSpaces}
                          />
                          <ErrorMessage
                            name="qualifications"
                            component="div"
                            className="text-danger"
                          />
                        </BootstrapForm.Group>
                      </Col>
                    </Row>
                  )}
                  {userType === 'Lab' && (
                    <Row className="mt-3">
                      <Col md={6}>
                        <BootstrapForm.Group controlId="labName">
                          <BootstrapForm.Label
                          className="fw-semibold primary-class"
                    >
                      Lab Name</BootstrapForm.Label>
                          <Field
                            name="labName"
                            as={BootstrapForm.Control}
                            onKeyDown={preventSpaces}
                            onPaste={preventPasteSpaces}
                          />
                          <ErrorMessage
                            name="labName"
                            component="div"
                            className="text-danger"
                          />
                        </BootstrapForm.Group>
                      </Col>
                      <Col md={6}>
                        <BootstrapForm.Group controlId="address">
                          <BootstrapForm.Label>Address</BootstrapForm.Label>
                          <Field
                            name="address"
                            as={BootstrapForm.Control}
                            onKeyDown={preventSpaces}
                            onPaste={preventPasteSpaces}
                          />
                          <ErrorMessage
                            name="address"
                            component="div"
                            className="text-danger"
                          />
                        </BootstrapForm.Group>
                      </Col>
                    </Row>
                  )}
                  {userType === 'Receptionist' && (
                    <Row className="mt-3">
                      <Col md={12}>
                        <BootstrapForm.Group controlId="address">
                          <BootstrapForm.Label 
                          className="fw-semibold primary-class"
                    >
                      Address</BootstrapForm.Label>
                          <Field
                            name="address"
                            as={BootstrapForm.Control}
                            onKeyDown={preventSpaces}
                            onPaste={preventPasteSpaces}
                          />
                          <ErrorMessage
                            name="address"
                            component="div"
                            className="text-danger"
                          />
                        </BootstrapForm.Group>
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-3">
                    <Col md={12}>
                      <BootstrapForm.Group controlId="subHospitalId">
                        <BootstrapForm.Label
                        className="fw-semibold primary-class"
                    >Subhospital</BootstrapForm.Label>
                        <Field
                          as="select"
                          name="subHospitalId"
                          className="form-control"
                        >
                          <option value="">Select Subhospital</option>
                          {subHospitals.map((sub) => (
                            <option
                              key={sub.sub_hospital_id}
                              value={sub.sub_hospital_id}
                            >
                              {sub.sub_hospital_name}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name="subHospitalId"
                          component="div"
                          className="text-danger"
                        />
                      </BootstrapForm.Group>
                    </Col>
                  </Row>
                  <Button type="submit" className="global-btn mt-3 w-100">
                    {editingUser
                      ? `Update ${userType}`
                      : `Add ${userType}`}
                  </Button>
                </FormikForm>
              )}
            </Formik>
          </Modal.Body>
        </Modal>

        <Row className="mt-4">
          <Col>
            {users.length === 0 ? (
              <p className="text-danger">No users found. Please add users.</p>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover className="AdminTable-root">
                  <thead>
                    <tr>
                      <th>Sr.No.</th>
                      <th>
                        Name
                        <BootstrapForm.Control
                        size="sm"
                        type="text"
                        placeholder="Filter"
                        className="mt-1"
                        value={filters.name}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === ' ' || e.key === 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          if (/^\s*$/.test(e.clipboardData.getData('text'))) {
                            e.preventDefault();
                          }
                        }}
                      />
                      </th>
                      <th>
                        Sub-Hospital
                        <BootstrapForm.Select
                          size="sm"
                          className="mt-1"
                          value={filters.subHospital}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              subHospital: e.target.value,
                            }))
                          }
                        >
                          <option value="">All</option>
                          {subHospitals.map((sub) => (
                            <option
                              key={sub.sub_hospital_id}
                              value={sub.sub_hospital_id}
                            >
                              {sub.sub_hospital_name}
                            </option>
                          ))}
                        </BootstrapForm.Select>
                      </th>
                      <th>
                        Role
                        <BootstrapForm.Select
                          size="sm"
                          className="mt-1"
                          value={filters.role}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              role: e.target.value,
                            }))
                          }
                        >
                          <option value="">All</option>
                          <option value="Doctor">Doctor</option>
                          <option value="Receptionist">Receptionist</option>
                          <option value="Lab">Lab</option>
                        </BootstrapForm.Select>
                      </th>
                      <th>
                        Contact Number
                        <BootstrapForm.Control
                          size="sm"
                          type="text"
                          placeholder="Filter"
                          className="mt-1"
                          value={filters.contact}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              contact: e.target.value,
                            }))
                          }
                        />
                      </th>
                      <th>
                        Email
                        <BootstrapForm.Control
                          size="sm"
                          type="text"
                          placeholder="Filter"
                          className="mt-1"
                          value={filters.email}
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                        />
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, index) => {
                      const matchedSubHospital = subHospitals.find(
                        (sub) =>
                          Number(sub.sub_hospital_id) ===
                          Number(user.sub_hospital_id)
                      );
                      return (
                        <tr key={user.id || index}>
                          <td>{index + 1}</td>
                          <td>{user.name || 'N/A'}</td>
                          <td>
                            {matchedSubHospital
                              ? matchedSubHospital.sub_hospital_name
                              : 'Unknown'}
                          </td>
                          <td>{user.userType || 'N/A'}</td>
                          <td>{user.contactNumber || 'N/A'}</td>
                          <td>{user.email || 'N/A'}</td>
                        <td>
                          <div className="d-flex gap-2">
                            {/* Edit */}
                            <button
                              onClick={() => handleEditUser(index)}
                              className="border-0 fw-semibold primary-class bg-transparent primary-class"
                              style={{ cursor: "pointer" }}
                            >
                              <span className="material-icons me-1" style={{ fontSize: 16 }}>
                                edit
                              </span>
                            </button>

                            {/* Delete */}
                            <DeleteDialog
                              path={`${BASE_URL}/api/admin/deleteUser/${user.userType}/${user.id}`}
                              message="User deleted successfully"
                              onSuccess={() =>
                                setUsers((prev) => prev.filter((u) => u.id !== user.id))
                              }
                              icon="custom"
                              
                            />
                            
                          

                            {/* Enable / Disable */}
                            <button
                              onClick={() => handleToggleUserStatus(user, user.status === 1 ? 0 : 1)}
                              className="border-0 bg-transparent p-0 d-flex align-items-center"
                              style={{ cursor: "pointer" }}
                            >
                              <span
                                className="material-icons"
                                style={{ fontSize: 18, color: user.status === 1 ? "red" : "green" }}
                              >
                                {user.status === 1 ? "block" : "check_circle"}
                              </span>
                            </button>
                          </div>
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ManageUsers;